import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { FileX, Target, Globe, ClipboardList, BarChart3, Trophy } from "lucide-react";
import { PAGE_CSS } from "@/pages/landing/landingStyles";




const FAQ_ITEMS = [
  {
    q: "Is TILPrep specific to the PoliTO TIL-I exam?",
    a: "Yes. TILPrep is built exclusively for the TIL-I — Politecnico di Torino's engineering admission test. Every practice question, mock exam, and explanation maps directly to the real exam structure: Mathematics, Logic, Physics, and Basic Technical Knowledge.",
  },
  {
    q: "What's the difference between Core and Pro?",
    a: "Core gives you 6 full mock exams plus topic-by-topic practice. Pro doubles the mock sets to 12. Both are one-time purchases — no subscription, no recurring billing.",
  },
  {
    q: "Is the content available in English?",
    a: "Yes! TILPrep is in English right now. We're working on full Italian support and it's coming in v2 — stay tuned!",
  },
  {
    q: "How accurate is the exam simulation?",
    a: "Questions are calibrated to match the difficulty, topic distribution, and distractor style of past TIL-I papers. Section timers are individual and non-cumulative — exactly as in the real exam. The scoring formula is identical. Our questions are intentionally slightly harder than the real thing so exam day feels familiar.",
  },
  {
    q: "When is the next TIL-I exam session?",
    a: "TIL-I exam sessions are held 5 times a year, typically in February, March, April, May, and July. Check the official PoliTO website for the exact dates of the current academic year.",
  },
  {
    q: "Who built this and why should I trust it?",
    a: "TILPrep was built by the team behind Life at PoliTO — a community of current PoliTO students who went through the same admissions process. The platform is not affiliated with or endorsed by Politecnico di Torino.",
  },
];

const LandingPage = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useDocumentMeta({
    title: "TILPrep — TIL-I Exam Prep for Politecnico di Torino",
    description:
      "Prepare for the TIL-I admission exam at Politecnico di Torino with full-length mock exams, real section timers, and the official scoring formula.",
    canonical: "https://tilprep.com/",
    ogUrl: "https://tilprep.com/",
    ogType: "website",
    jsonLdId: "landing-faq-ld",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQ_ITEMS.map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: { "@type": "Answer", text: item.a },
      })),
    },
  });

  // Hero text + fade-up animations
  useEffect(() => {
    const root = document.querySelector(".tilprep-landing");
    if (!root) return;
    const h1 = root.querySelector(".hero-h1") as HTMLElement | null;
    if (!h1) return;

    let ci = 0;
    const stagger = 0.025;

    const wrapChars = (node: ChildNode) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const frag = document.createDocumentFragment();
        const words = (node.textContent || "").split(" ");
        words.forEach((word, wi) => {
          if (word === "") return;
          const wordSpan = document.createElement("span");
          wordSpan.style.cssText = "display:inline-block; white-space:nowrap;";
          for (const char of word) {
            const inner = document.createElement("span");
            inner.className = "char-inner";
            inner.style.animationDelay = ci++ * stagger + "s";
            inner.textContent = char;
            wordSpan.appendChild(inner);
          }
          frag.appendChild(wordSpan);
          if (wi < words.length - 1) frag.appendChild(document.createTextNode(" "));
        });
        node.parentNode?.replaceChild(frag, node);
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        Array.from(node.childNodes).forEach(wrapChars);
      }
    };

    Array.from(h1.childNodes).forEach(wrapChars);
    h1.style.opacity = "1";

    const h1End = ci * stagger;
    const fadeTargets: [string, number][] = [
      [".dream-h2", 0],
      [".dream-sub", 0.1],
      [".dream-stats", 0.2],
      [".hero-sub", h1End - 0.1],
      [".hero-actions", h1End + 0.05],
      [".hero-card", 0.2],
    ];
    fadeTargets.forEach(([sel, delay]) => {
      const el = root.querySelector(sel) as HTMLElement | null;
      if (!el) return;
      el.style.animationDelay = Math.max(0, delay) + "s";
      el.classList.add("fade-up-hero");
    });
  }, []);

  return (
    <div className="tilprep-landing">
      <style dangerouslySetInnerHTML={{ __html: PAGE_CSS }} />

      {/* THE DREAM (light) */}
      <section className="hero">
        <div className="dream-glow" />
        <div className="dream-inner">
          <h1 className="dream-h2 fade-up-hero font-extrabold">
            One of Europe’s finest engineering universities. One exam standing in your <em>way.</em>
          </h1>
          <p className="dream-sub">
            Politecnico di Torino consistently ranks among Europe’s top technical universities. Its graduates build the
            infrastructure, vehicles, and systems that define the modern world. This is the degree worth fighting for.
          </p>
          <div className="dream-stats">
            <div className="dream-stat">
              <div className="dream-stat-num">
                #5<em>3</em>
              </div>
              <div className="dream-stat-lbl">QS World University Rankings — Engineering</div>
            </div>
            <div className="dream-stat">
              <div className="dream-stat-num">
                40<em>k+</em>
              </div>
              <div className="dream-stat-lbl">Students from 135 countries</div>
            </div>
            <div className="dream-stat">
              <div className="dream-stat-num">
                96<em>%</em>
              </div>
              <div className="dream-stat-lbl">Graduate employment rate</div>
            </div>
            <div className="dream-stat">
              <div className="dream-stat-num">
                18<em>59</em>
              </div>
              <div className="dream-stat-lbl">Founded — over 165 years of excellence</div>
            </div>
          </div>
        </div>
      </section>

      {/* HERO (navy) */}
      <section className="dream" id="home">
        <div className="hero-glow" />
        <div className="hero-inner">
          <div>
            <p className="hero-label">The TIL-I Preparation Platform</p>
            <h2 className="hero-h1 font-extrabold">
              The exam between you and <em>PoliTO.</em>
            </h2>
            <p className="hero-sub">
              The only platform built exclusively for the TIL-I engineering admission test. Realistic simulations,
              section analytics, and weekly student rankings — in Italian and English.
            </p>
            <div className="hero-actions">
              <Link to="/exam" className="btn-primary">
                Test Your Level - For Free
              </Link>
              <Link to="/pricing" className="btn-ghost">
                See Pricing
              </Link>
            </div>
          </div>
          <div className="hero-card">
            <div className="hero-card-content">
              <div className="card-eyebrow">Ranking preview</div>
              <div className="card-title font-bold">Is your name already on PoliTO’s acceptance list?</div>
              <div className="card-sub">
                A dreamy glimpse of your ranking signal, presented like the moment you reach the list.
              </div>
            </div>
            <div className="hero-card-image">
              <img src="/ranking.png" alt="PoliTO acceptance ranking preview" />
            </div>
          </div>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="problem">
        <div className="section-inner">
          <h2 className="section-h2 font-extrabold">
            Most students walk in <em>blind.</em>
          </h2>
          <p className="section-sub">
            The TIL-I is non-trivial and non-negotiable. Yet the resources available to prepare for it are shockingly
            inadequate — especially for international students.
          </p>
          <div className="problem-grid">
            <div className="problem-card">
              <div className="problem-icon"><FileX size={24} style={{ color: "var(--navy)" }} /></div>
              <div className="problem-title">The practice material doesn't match the real exam</div>
              <div className="problem-desc">
                Generic Italian prep sites use different question formats, different difficulty levels, and completely
                ignore the section-timer mechanic that makes the TIL-I uniquely challenging.
              </div>
              <div className="problem-quote">"I practiced for weeks and still felt unprepared on the day."</div>
            </div>
            <div className="problem-card">
              <div className="problem-icon"><Target size={24} style={{ color: "var(--navy)" }} /></div>
              <div className="problem-title">You have no idea if you're actually ready</div>
              <div className="problem-desc">
                There's no readiness signal. No benchmark. No way to know whether your score puts you in the top 20% of
                applicants or whether you'll miss the 30-point minimum to even appear in the ranking.
              </div>
              <div className="problem-quote">"I thought I was prepared. My score said otherwise."</div>
            </div>
            <div className="problem-card">
              <div className="problem-icon"><Globe size={24} style={{ color: "var(--navy)" }} /></div>
              <div className="problem-title">International students start at a disadvantage</div>
              <div className="problem-desc">
                Almost every preparation resource is Italian-only. International students sit the same exam — with less
                support, less context, and less time to find quality bilingual material.
              </div>
              <div className="problem-quote">"I couldn't find a single resource in English."</div>
            </div>
          </div>
        </div>
      </section>

      {/* EXAM REALITY */}
      <section className="exam-reality">
        <div className="section-inner">
          <h2 className="section-h2 font-extrabold">
            This is what you're <em>actually</em> walking into.
          </h2>
          <p className="section-sub">
            The TIL-I is more structured and more demanding than most students expect. TILPrep simulates every detail —
            including the one mechanic that trips most people up: separate non-cumulative timers per section.
          </p>
          <div className="exam-sections">
            <div className="exam-sec-card">
              <div className="exam-sec-tag">Section 01</div>
              <div className="exam-sec-name">Mathematics</div>
              <div className="exam-sec-qs">16</div>
              <div className="exam-sec-time">36 MINUTES</div>
            </div>
            <div className="exam-sec-card">
              <div className="exam-sec-tag">Section 02</div>
              <div className="exam-sec-name">Reading Comprehension & Logic</div>
              <div className="exam-sec-qs">10</div>
              <div className="exam-sec-time">20 MINUTES</div>
            </div>
            <div className="exam-sec-card">
              <div className="exam-sec-tag">Section 03</div>
              <div className="exam-sec-name">Physics</div>
              <div className="exam-sec-qs">10</div>
              <div className="exam-sec-time">22 MINUTES</div>
            </div>
            <div className="exam-sec-card">
              <div className="exam-sec-tag">Section 04</div>
              <div className="exam-sec-name">Basic Technical Knowledge</div>
              <div className="exam-sec-qs">6</div>
              <div className="exam-sec-time">12 MINUTES</div>
            </div>
          </div>
          <div className="exam-summary">
            <div className="exam-sum-item">
              <div className="exam-sum-lbl">Total Questions</div>
              <div className="exam-sum-val">
                42 <small>questions</small>
              </div>
            </div>
            <div className="exam-sum-item">
              <div className="exam-sum-lbl">Total Duration</div>
              <div className="exam-sum-val">
                90 <small>minutes</small>
              </div>
            </div>
            <div className="exam-sum-item">
              <div className="exam-sum-lbl">Ranking Threshold</div>
              <div className="exam-sum-val">
                30 <small>/ 100 minimum</small>
              </div>
            </div>
            <div className="exam-sum-item">
              <div className="exam-sum-lbl">Languages</div>
              <div className="exam-sum-val" style={{ fontSize: 18, letterSpacing: "-.5px" }}>
                IT + EN <small>both available</small>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SOLUTION */}
      <section className="solution">
        <div className="section-inner">
          <h2 className="section-h2 font-extrabold">
            TILPrep gives you the <em>full picture.</em>
          </h2>
          <p className="section-sub">
            Not a question bank. Not a flashcard app. A complete preparation system that tells you exactly where you
            stand before you walk into the exam room.
          </p>
          <div className="sol-grid">
            <div className="sol-card dark">
              <div className="sol-num">01</div>
              <div className="sol-icon"><ClipboardList size={22} style={{ color: "var(--emerald)" }} /></div>
              <div className="sol-title">Realistic Full Simulations</div>
              <div className="sol-desc">
                Every mock exam mirrors the exact TIL-I structure — 42 questions, 4 sections, separate countdown timers,
                and authentic difficulty. You practice the real exam, not a simplified version of it.
              </div>
              <span className="sol-tag">Free + Core + Pro</span>
            </div>
            <div className="sol-card">
              <div className="sol-num">02</div>
              <div className="sol-icon"><BarChart3 size={22} style={{ color: "var(--emerald)" }} /></div>
              <div className="sol-title">Know Your Weakest Topic Before Exam Day</div>
              <div className="sol-desc">
                Section-level accuracy, score trends over time, and a readiness indicator that tells you whether you're
                on track. Stop guessing. Start knowing.
              </div>
              <span className="sol-tag">Core + Pro</span>
            </div>
            <div className="sol-card">
              <div className="sol-num">03</div>
              <div className="sol-icon"><Trophy size={22} style={{ color: "var(--emerald)" }} /></div>
              <div className="sol-title">See How You Rank Against Real Applicants</div>
              <div className="sol-desc">
                A weekly leaderboard of every student preparing for the same exam. The only way to know if your score is
                competitive before you find out the hard way.
              </div>
              <span className="sol-tag">Core + Pro</span>
            </div>
          </div>
        </div>
      </section>

      {/* FREE CTA */}
      <section className="free-cta">
        <div className="free-cta-inner">
          <span className="eyebrow-amber">Start Right Now — IT'S FREE</span>
          <h2 className="free-cta-h2 font-extrabold">
            Find out where you <em>actually</em> stand.
          </h2>
          <p className="free-cta-sub">
            Take a full 42-question TIL-I simulation. No credit card. No commitment.
            <br />
            Get your results and see exactly what needs work.
          </p>
          <Link to="/exam" className="btn-primary" style={{ fontSize: 17, padding: "18px 44px" }}>
            Take the Free Exam →
          </Link>
          <div className="free-pillars">
            <span className="pillar">Full 42-question simulation</span>
            <span className="pillar">Real section timers</span>
            <span className="pillar">Italian & English</span>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="faq">
        <div className="faq-inner">
          <div className="faq-header">
            <div className="eyebrow-amber">Questions?</div>
            <h2 className="faq-title font-extrabold">
              Everything you <em>need</em> to know.
            </h2>
          </div>
          <div className="faq-grid">
            {FAQ_ITEMS.map((item, i) => (
              <button
                key={i}
                type="button"
                className={`faq-item${openFaq === i ? " active" : ""}`}
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
              >
                <div className="faq-q">{item.q}</div>
                <div className="faq-a">{item.a}</div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="final-cta">
        <div className="final-cta-inner">
          <div className="eyebrow-amber">Your PoliTO Journey Starts Here</div>
          <h2 className="final-h2 font-extrabold">
            Don't walk into the exam
            <br />
            <em>unprepared.</em>
          </h2>
          <p className="final-sub">1000+ students are already preparing. See which plan fits your timeline.</p>
          <div className="final-actions">
            <Link to="/pricing" className="btn-primary" style={{ fontSize: 17, padding: "18px 44px" }}>
              See Pricing
            </Link>
            <Link to="/exam" className="btn-white-ghost">
              Try Free First
            </Link>
          </div>
        </div>
      </section>

    </div>
  );
};

export default LandingPage;
